import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  AlertTriangle,
  ClipboardCheck,
  FileWarning,
  ListTodo,
  CalendarClock,
  Plus,
  FileSpreadsheet,
  UserPlus,
  Activity as ActivityIcon,
  CheckCircle2,
  Info,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { KpiCard } from "../components/dashboard/KpiCard";
import {
  MonthlyComplianceChart,
  ComplianceTrendChart,
  RiskDistributionChart,
} from "../components/dashboard/Charts";
import CalendarWidget from "../components/dashboard/CalendarWidget";
import ReadinessCard from "../components/dashboard/ReadinessCard";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "../lib/utils";

const activityIcon = {
  critical: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};
const activityColor = {
  critical: "text-red-600 bg-red-100 dark:bg-red-950/40",
  warning: "text-amber-600 bg-amber-100 dark:bg-amber-950/40",
  info: "text-blue-600 bg-blue-100 dark:bg-blue-950/40",
  success: "text-green-600 bg-green-100 dark:bg-green-950/40",
};

const statusBadge = {
  planned: { v: "neutral", label: "Planned" },
  in_progress: { v: "info", label: "In progress" },
  completed: { v: "success", label: "Completed" },
  todo: { v: "neutral", label: "To do" },
  done: { v: "success", label: "Done" },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dash, act, rd] = await Promise.all([
          api.get("/dashboard"),
          api.get("/activities?limit=8"),
          api.get("/readiness"),
        ]);
        setData(dash.data);
        setActivities(act.data.items);
        setReadiness(rd.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`dash-skel-${i}`} className="card-surface h-28 animate-pulse" />
        ))}
      </div>
    );
  }

  const k = data.kpis;
  const calendarEvents = data.upcoming_deadlines.map((u) => ({
    date: u.due_date,
    kind: u.kind,
    title: u.title,
  }));

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
            Welcome back, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Here&apos;s a snapshot of your organisation&apos;s compliance posture as of{" "}
            {format(new Date(), "EEEE, MMM d, yyyy")}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="dashboard-export"
            onClick={() => window.print()}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export report
          </Button>
          <Button size="sm" data-testid="dashboard-new-audit" onClick={() => navigate("/audits")}>
            <Plus className="w-4 h-4" />
            New audit
          </Button>
        </div>
      </div>

      {/* Readiness score */}
      <ReadinessCard data={readiness} />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard
          testId="kpi-compliance-score"
          title="Compliance Score"
          value={k.compliance_score}
          suffix="/ 100"
          icon={ShieldCheck}
          kind="info"
          delta="Weighted across audits & findings"
        />
        <KpiCard
          testId="kpi-critical-risks"
          title="Critical Risks"
          value={k.critical_risks}
          icon={AlertTriangle}
          kind="critical"
          delta="Open & mitigating"
        />
        <KpiCard
          testId="kpi-pending-audits"
          title="Pending Audits"
          value={k.pending_audits}
          icon={ClipboardCheck}
          kind="warning"
          delta="Planned or in progress"
        />
        <KpiCard
          testId="kpi-open-findings"
          title="Open Findings"
          value={k.open_findings}
          icon={FileWarning}
          kind="critical"
          delta="Includes items in review"
        />
        <KpiCard
          testId="kpi-pending-tasks"
          title="Pending Tasks"
          value={k.pending_tasks}
          icon={ListTodo}
          kind="neutral"
          delta="Across all owners"
        />
        <KpiCard
          testId="kpi-upcoming-deadlines"
          title="Upcoming Deadlines"
          value={k.upcoming_deadlines}
          icon={CalendarClock}
          kind="warning"
          delta="Within next 30 days"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-monthly-compliance">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Monthly compliance activity</CardTitle>
                <CardDescription>Audits kicked off vs. findings raised, last 6 months</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <MonthlyComplianceChart data={data.monthly_compliance} />
          </CardContent>
        </Card>

        <Card data-testid="chart-risk-distribution">
          <CardHeader>
            <CardTitle>Risk distribution</CardTitle>
            <CardDescription>Open risks by severity</CardDescription>
          </CardHeader>
          <CardContent>
            <RiskDistributionChart data={data.risk_distribution} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="chart-compliance-trend">
          <CardHeader>
            <CardTitle>Compliance score trend</CardTitle>
            <CardDescription>Rolling monthly compliance score</CardDescription>
          </CardHeader>
          <CardContent>
            <ComplianceTrendChart data={data.compliance_trend} />
          </CardContent>
        </Card>

        {/* Audit progress list */}
        <Card data-testid="chart-audit-progress">
          <CardHeader>
            <CardTitle>Audit progress</CardTitle>
            <CardDescription>Top active audits</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {data.audit_progress.map((a) => (
                <li key={a.id} data-testid={`audit-progress-${a.id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.title}</div>
                      <div className="text-xs text-muted-foreground">{a.framework}</div>
                    </div>
                    <span className="tabular-nums text-xs font-semibold text-foreground">
                      {a.progress}%
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        a.progress === 100 ? "bg-green-500" : "bg-primary"
                      )}
                      style={{ width: `${a.progress}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Bottom grid: activities, deadlines, calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Activity timeline */}
        <Card className="lg:col-span-2" data-testid="recent-activity">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent activity</CardTitle>
                <CardDescription>Latest actions across the platform</CardDescription>
              </div>
              <ActivityIcon className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <ol className="relative border-l border-border ml-3 space-y-4">
              {activities.map((a) => {
                const Icon = activityIcon[a.kind] || Info;
                return (
                  <li key={a.id} className="ml-4">
                    <span
                      className={cn(
                        "absolute -left-3 w-6 h-6 rounded-full flex items-center justify-center border border-border bg-card",
                        activityColor[a.kind] || activityColor.info
                      )}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="text-sm">
                      <span className="font-medium">{a.actor_name}</span>{" "}
                      <span className="text-muted-foreground">{a.action}</span>{" "}
                      <span className="font-medium">{a.target}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>

        {/* Quick actions + Calendar stacked */}
        <div className="space-y-4">
          <Card data-testid="quick-actions">
            <CardHeader>
              <CardTitle>Quick actions</CardTitle>
              <CardDescription>Common workflows</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                <QAButton
                  testId="qa-new-audit"
                  onClick={() => navigate("/audits")}
                  icon={ClipboardCheck}
                  label="New audit"
                />
                <QAButton
                  testId="qa-log-risk"
                  onClick={() => navigate("/risks")}
                  icon={AlertTriangle}
                  label="Log risk"
                />
                <QAButton
                  testId="qa-add-finding"
                  onClick={() => navigate("/findings")}
                  icon={FileWarning}
                  label="Add finding"
                />
                {user?.role === "admin" && (
                  <QAButton
                    testId="qa-invite-user"
                    onClick={() => navigate("/users")}
                    icon={UserPlus}
                    label="Invite user"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="calendar-card">
            <CardHeader>
              <CardTitle>Calendar</CardTitle>
              <CardDescription>Upcoming audits & tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <CalendarWidget events={calendarEvents} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upcoming deadlines table */}
      <Card data-testid="upcoming-deadlines-card">
        <CardHeader>
          <CardTitle>Upcoming deadlines</CardTitle>
          <CardDescription>Audits and tasks due in the next 30 days</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b border-border bg-muted/40">
                  <th className="text-left py-2.5 px-6 label-eyebrow">Title</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Type</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-6 label-eyebrow">Due</th>
                </tr>
              </thead>
              <tbody>
                {data.upcoming_deadlines.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground">
                      Nothing due in the next 30 days.
                    </td>
                  </tr>
                )}
                {data.upcoming_deadlines.map((u) => {
                  const s = statusBadge[u.status] || { v: "neutral", label: u.status };
                  return (
                    <tr
                      key={u.id}
                      className="border-b border-border hover:bg-muted/40 transition-colors"
                      data-testid={`deadline-row-${u.id}`}
                    >
                      <td className="py-3 px-6 font-medium">{u.title}</td>
                      <td className="py-3 px-6 capitalize text-muted-foreground">{u.kind}</td>
                      <td className="py-3 px-6">
                        <Badge variant={s.v}>{s.label}</Badge>
                      </td>
                      <td className="py-3 px-6 tabular-nums text-muted-foreground">
                        {format(new Date(u.due_date), "MMM d, yyyy")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QAButton({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex items-center gap-2 h-10 px-3 rounded-md border border-border hover:bg-muted transition-colors text-left"
    >
      <span className="w-7 h-7 rounded-md bg-primary-subtle text-primary flex items-center justify-center dark:bg-primary/15">
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-sm font-medium truncate">{label}</span>
    </button>
  );
}
